// src/components/Header.js
import "./Header.css";
import React, { useState } from "react";
import { Navbar, Nav, NavDropdown, Container } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUserCircle,
  faSignOutAlt,
  faChartBar,
  faCreditCard,
  faList,
  faFileAlt,
  faShoppingCart,
  faPlane,
  faCalendarAlt,
} from "@fortawesome/free-solid-svg-icons";
import CodeLookupModal from "../search/CodeLookupModal";

const Header = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [showCodeLookup, setShowCodeLookup] = useState(false);

  // Helper function to get JWT token for authenticated requests
  const getAuthHeaders = () => {
    const token = localStorage.getItem('jwtToken');
    const tokenType = localStorage.getItem('tokenType') || 'Bearer';
    
    const headers = {
      'Content-Type': 'application/json',
    };
    
    if (token) {
      headers.Authorization = `${tokenType} ${token}`;
    }
    
    return headers;
  };

  const handleCreateUser = () => {
    navigate("/create-user");
  };

  const handleLogout = () => {
    // Clear JWT token and related data
    localStorage.removeItem('jwtToken');
    localStorage.removeItem('tokenType');
    localStorage.removeItem('userId');
    localStorage.removeItem('username');
    localStorage.removeItem('tokenExpiration');
    
    // Clear any other user-related data
    localStorage.removeItem('userPreferences');
    localStorage.removeItem('userSettings');
    
    // Call the parent logout function
    onLogout();
    navigate("/login");
  };

  const handlePayToSubscribe = () => {
    if (user && user.activeSubscription) {
      navigate("/subscriptions");
    } else {
      navigate("/pricing");
    }
  };

  const handleManageSubscription = async () => {
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL;
      const endpoint = `${baseUrl}/api/stripe/create-portal-session?customerId=${encodeURIComponent(
        user.stripeCustomerId
      )}`;

      // Use JWT token for authenticated request
      const response = await fetch(endpoint, {
        method: "POST",
        headers: getAuthHeaders(),
      });

      if (response.status === 401) {
        console.log('Authentication failed, logging out...');
        handleLogout();
        return;
      }

      if (!response.ok) {
        throw new Error(`Request failed: ${response.status}`);
      }

      const data = await response.json();
      window.location.href = data.url; // redirect to Stripe portal
    } catch (err) {
      console.error("Failed to create portal session", err);
      
      // If it's an auth error, logout the user
      if (err.message.includes('401') || err.message.includes('Unauthorized')) {
        handleLogout();
      }
    }
  };

  const handleCodeSelect = (code, name, type) => {
    // Copy code to clipboard
    navigator.clipboard.writeText(code).then(() => {
      console.log(`${type} code ${code} (${name}) copied to clipboard`);
    }).catch(err => {
      console.error('Failed to copy to clipboard:', err);
    });
  };

  return (
    <>
      <Navbar variant="light" expand="lg" className="custom-bg mb-4 shadow-sm">
        <Container>
          <Navbar.Brand
            as={Link}
            to="/dashboard"
            className="text-white fw-bold fs-3"
          >
            Sked Guy
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="navbar-nav" />
          <Navbar.Collapse id="navbar-nav">
            <Nav className="text-white me-auto">
              {/* Reports Menu - Show for all logged-in users with active subscription */}
              {user && user.activeSubscription && (
                <NavDropdown
                  title={
                    <span>
                      <FontAwesomeIcon icon={faChartBar} className="me-2" />
                      Reports
                    </span>
                  }
                  id="reports-dropdown"
                >
                  <NavDropdown.Item as={Link} to="/dashboard">
                    <FontAwesomeIcon icon={faCalendarAlt} className="me-2" />
                    Flight Schedules
                  </NavDropdown.Item>
                  <NavDropdown.Item as={Link} to="/flightstatus">
                    <FontAwesomeIcon icon={faPlane} className="me-2" />
                    Flight Status
                  </NavDropdown.Item>
                </NavDropdown>
              )}

              {/* Admin Menus */}
              {user && user.role === "Admin" && (
                <>
                  <NavDropdown
                    title={
                      <span>
                        <FontAwesomeIcon icon={faFileAlt} className="me-2" />
                        Log Management
                      </span>
                    }
                    id="user-management-dropdown"
                  >
                    <NavDropdown.Item as={Link} to="/logs">
                      <FontAwesomeIcon icon={faList} className="me-2" />
                      View Logs
                    </NavDropdown.Item>
                    <NavDropdown.Item as={Link} to="/report-logs">
                      <FontAwesomeIcon icon={faChartBar} className="me-2" />
                      View Report Logs
                    </NavDropdown.Item>
                  </NavDropdown>
                </>
              )}

              {/* Payment Options - Show for all logged-in users */}
              {user && user.activeSubscription && (
                <NavDropdown
                  title={
                    <span>
                      <FontAwesomeIcon icon={faCreditCard} className="me-2" />
                      Payments
                    </span>
                  }
                  id="payments-dropdown"
                >
                  <NavDropdown.Item as={Link} to="/one-time-payment">
                    <FontAwesomeIcon icon={faShoppingCart} className="me-2" />
                    One-Time Payment
                  </NavDropdown.Item>
                  <NavDropdown.Divider />
                  <NavDropdown.Item onClick={handleManageSubscription}>
                    <FontAwesomeIcon icon={faCreditCard} className="me-2" />
                    Manage Subscription
                  </NavDropdown.Item>
                </NavDropdown>
              )}

              {/* Code Lookup - Show for all logged-in users */}
              {user && user.activeSubscription && (
                <Nav.Link 
                  onClick={() => setShowCodeLookup(true)}
                  className="text-white"
                  style={{ cursor: 'pointer' }}
                >
                  <span className="me-2">✈️</span>
                  Code Lookup
                </Nav.Link>
              )}

            </Nav>

            {/* User Info and Logout */}
            <Nav className="ms-auto">
              {user ? (
                <NavDropdown
                  title={
                    <span>
                      <FontAwesomeIcon icon={faUserCircle} className="me-2" />
                      {user.username}
                    </span>
                  }
                  id="user-dropdown"
                  align="end"
                  menuVariant="dark"
                >
                  <NavDropdown.Item onClick={handleLogout}>
                    <FontAwesomeIcon icon={faSignOutAlt} className="me-2" />
                    Logout
                  </NavDropdown.Item>
                </NavDropdown>
              ) : (
                <Nav.Link as={Link} to="/login" className="text-white">
                  Login
                </Nav.Link>
              )}
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      {/* Code Lookup Modal */}
      <CodeLookupModal
        show={showCodeLookup}
        onHide={() => setShowCodeLookup(false)}
        onSelect={handleCodeSelect}
      />
    </>
  );
};

export default Header;